import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { UiErrorParser } from '../ui-error-parser';
import { SICOM_PATHS } from 'src/sicom-paths';
import { normalizeReportMessage } from '../../../app/shared/utils/report-message.util';

/**
 * Traduz erros de geração de relatório PDF (arrecadação, cadastro/médico e cadastro/prestador)
 * em toast de UI.
 *
 * Regra de emissão não atendida (ex.: prestador sem diretor técnico) chega como 4xx: a mensagem
 * pode vir em `error.message` (ExportResponse do backend) ou em `error.title` (CfmErrorDto do
 * handler global quando `CfmCustomException` carrega a mensagem no título) — ambos são cobertos
 * aqui. O `detail` é ignorado de propósito: para `CfmCustomException` ele é a lista de `errors`
 * serializada em Java, o que renderiza `[]` ou `[mensagem]` na tela.
 */
export class RelatorioErrorParser extends UiErrorParser {
    private readonly relatorioUrls = [
        SICOM_PATHS.financeiro.arrecadacao.relatorios,
        SICOM_PATHS.financeiro.dividaAtiva.relatorios,
        SICOM_PATHS.registro.medico.reports.base,
        SICOM_PATHS.registro.prestador.reports.base
    ];

    public override verifyContext(_error: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
        return this.relatorioUrls.some((url) => req.url.includes(url));
    }

    public override beforeTextBuild(errorResponse: HttpErrorResponse, _req: HttpRequest<unknown>): void {
        this.setMessageStart('');
        this.setMethod('');
        this.setTarget('');

        // Regra de negócio (4xx) é aviso ao usuário; falha de servidor (5xx) é erro.
        const isRegraDeNegocio = errorResponse.status >= 400 && errorResponse.status < 500;
        const mensagem = this.extrairMensagemRelatorio(errorResponse);

        if (mensagem) {
            this.setTitle(isRegraDeNegocio ? 'Atenção' : 'Erro');
            this.setSeverity(isRegraDeNegocio ? 'warn' : 'error');
            this.setMessageStart(mensagem);
        } else {
            this.setTitle('Erro');
            this.setSeverity('error');
            this.setMessageStart('Falha ao gerar o arquivo PDF.');
        }
    }

    public override errorComplement(_error: HttpErrorResponse, _req: HttpRequest<unknown>): string {
        return '';
    }

    /**
     * Motivo do backend: `message` (ExportResponse) ou `title` (CfmErrorDto) — o que vier
     * preenchido. Ambos passam por `normalizeReportMessage` porque, em alguns pontos do backend,
     * também chegam embrulhados em colchetes como `List#toString()` (ex.: "[]"), o mesmo problema
     * já tratado para `detail`.
     */
    private extrairMensagemRelatorio(errorResponse: HttpErrorResponse): string {
        const corpo = errorResponse.error as { message?: unknown; title?: unknown } | null | undefined;
        return normalizeReportMessage(corpo?.message) || normalizeReportMessage(corpo?.title);
    }
}
